import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../../../components/Layout';
import Modal from '../../../components/Modal';
import ApiInspector from '../../../components/ApiInspector';
import BopRowActions, { RowIcon } from '../../../components/BopRowActions';
import BopExcelTable, { BopExcelColumn } from '../../../components/BopExcelTable';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { formatCurrency } from '../../../utils/formatters';
import { API_DOCS } from './ContrattiInfragruppoPage.api';

// ── Tipi ──────────────────────────────────────────────────────────────────
interface ContrattoInfragruppo {
  id: number;
  societaId: number;
  societaNome?: string;
  fornitore: string;
  nomeServizio: string;
  importoMensile: number;
  daData: string;
  aData: string;
  attivo: boolean;
}

interface FormData {
  societaId: number;
  fornitore: string;
  nomeServizio: string;
  importoMensile: string;
  daData: string;
  aData: string;
}

function emptyForm(societaId = 0, anno = new Date().getFullYear()): FormData {
  return {
    societaId,
    fornitore: '',
    nomeServizio: '',
    importoMensile: '',
    daData: `${anno}-01-01`,
    aData: `${anno}-12-31`,
  };
}

const MODULO = 'management.contratti_infragruppo';

const fmtData = (d: string) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export default function ContrattiInfragruppoPage() {
  const { can } = usePermesso();
  const canEdit = can(MODULO, 'edit');
  const canDelete = can(MODULO, 'delete');

  const {
    societaId: filtroSocietaId,
    setSocietaId: setFiltroSocietaId,
    listaSocieta: societa,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  // Lista anni disponibili: anno corrente + 4 precedenti + 1 futuro
  const annoCorrente = new Date().getFullYear();
  const anniSelezionabili = useMemo(() => {
    const arr: number[] = [];
    for (let a = annoCorrente + 1; a >= annoCorrente - 4; a--) arr.push(a);
    return arr;
  }, [annoCorrente]);

  const [filtroAnno, setFiltroAnno] = useState<string>(String(annoCorrente));
  const [filtroFornitore, setFiltroFornitore] = useState('');
  const [mostraDisattivi, setMostraDisattivi] = useState(false);

  const [items, setItems] = useState<ContrattoInfragruppo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm());
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!pronta) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filtroSocietaId) params.set('societaId', filtroSocietaId);
      if (filtroAnno) params.set('anno', filtroAnno);
      if (filtroFornitore.trim()) params.set('fornitore', filtroFornitore.trim());
      if (mostraDisattivi) params.set('mostraDisattivi', 'true');

      // @api GET /api/contratti-infragruppo?societaId=&anno=&fornitore=&mostraDisattivi= -> { success: true, data: [{ id, societaId, societaNome, fornitore, nomeServizio, importoMensile, daData, aData, attivo }] }
      const res = await apiFetch<{ success: boolean; data: ContrattoInfragruppo[] }>(
        `/api/contratti-infragruppo?${params}`,
      );
      setItems(res.data ?? []);
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [pronta, filtroSocietaId, filtroAnno, filtroFornitore, mostraDisattivi]);

  // ── Modale crea/modifica ────────────────────────────────────────────────
  const openCreate = () => {
    const sid = filtroSocietaId ? Number(filtroSocietaId) : (societa[0]?.id ?? 0);
    const anno = filtroAnno ? Number(filtroAnno) : annoCorrente;
    setForm(emptyForm(sid, anno));
    setEditId(null);
    setFormError('');
    setModalOpen(true);
  };

  const openEdit = (c: ContrattoInfragruppo) => {
    setForm({
      societaId: c.societaId,
      fornitore: c.fornitore,
      nomeServizio: c.nomeServizio,
      importoMensile: String(c.importoMensile),
      daData: c.daData.slice(0, 10),
      aData: c.aData.slice(0, 10),
    });
    setEditId(c.id);
    setFormError('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setFormError('');
  };

  const save = async () => {
    if (!form.societaId) { setFormError('Seleziona una società'); return; }
    if (!form.fornitore.trim()) { setFormError('Fornitore obbligatorio'); return; }
    if (!form.nomeServizio.trim()) { setFormError('Nome servizio obbligatorio'); return; }
    const importoNum = Number(form.importoMensile.replace(',', '.'));
    if (!Number.isFinite(importoNum) || importoNum < 0) {
      setFormError('Importo mensile non valido');
      return;
    }
    if (!form.daData || !form.aData) { setFormError('Date obbligatorie'); return; }
    if (new Date(form.aData) < new Date(form.daData)) {
      setFormError('La data "a" deve essere >= della data "da"');
      return;
    }

    setSaving(true);
    setFormError('');
    try {
      const payload = {
        fornitore: form.fornitore.trim(),
        nomeServizio: form.nomeServizio.trim(),
        importoMensile: importoNum,
        daData: form.daData,
        aData: form.aData,
      };
      if (editId) {
        // @api PUT /api/contratti-infragruppo/:id { fornitore, nomeServizio, importoMensile, daData, aData } -> { success: true, data: { id, ... } }
        await apiFetch(`/api/contratti-infragruppo/${editId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } else {
        // @api POST /api/contratti-infragruppo { societaId, fornitore, nomeServizio, importoMensile, daData, aData } -> { success: true, data: { id, ... } }
        await apiFetch('/api/contratti-infragruppo', {
          method: 'POST',
          body: JSON.stringify({ ...payload, societaId: form.societaId }),
        });
      }
      closeModal();
      load();
    } catch (e: any) {
      setFormError(e.message || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const elimina = async (c: ContrattoInfragruppo) => {
    if (!window.confirm(`Eliminare il contratto "${c.fornitore} — ${c.nomeServizio}"?`)) return;
    try {
      // @api DELETE /api/contratti-infragruppo/:id -> { success: true }
      await apiFetch(`/api/contratti-infragruppo/${c.id}`, { method: 'DELETE' });
      load();
    } catch (e: any) {
      setError(e.message || 'Errore nell\'eliminazione');
    }
  };

  // ── Riepilogo: totale anno + media mensile + snapshot a una data ─────────
  // Le righe possono coprire frazioni di anno o essere lo stesso servizio in
  // periodi diversi: il "totale mensile" come semplice somma non avrebbe senso.
  // Calcoliamo invece il costo dell'anno (importoMensile × mesi attivi nell'anno),
  // la media mensile (totale/12) e uno snapshot a una data specifica.
  const stats = useMemo(() => {
    if (items.length === 0) return null;
    const oggi = new Date();
    const annoOggi = oggi.getFullYear();
    const annoSel = filtroAnno ? Number(filtroAnno) : null;

    // Snapshot: oggi se anno corrente o filtro "tutti", 1°gen se futuro, 31dic se passato
    let snapshot: Date;
    if (annoSel == null || annoSel === annoOggi) snapshot = oggi;
    else if (annoSel > annoOggi) snapshot = new Date(annoSel, 0, 1);
    else snapshot = new Date(annoSel, 11, 31);

    let totaleAnno = 0;
    let mensileSnapshot = 0;
    for (const c of items) {
      const da = new Date(c.daData);
      const a = new Date(c.aData);
      const importo = Number(c.importoMensile || 0);

      // Mesi attivi nell'anno selezionato: per ogni mese, attivo se almeno
      // un giorno del mese rientra in [daData, aData]
      if (annoSel != null) {
        let mesi = 0;
        for (let m = 0; m < 12; m++) {
          const inizioMese = new Date(annoSel, m, 1);
          const fineMese = new Date(annoSel, m + 1, 0);
          if (a >= inizioMese && da <= fineMese) mesi++;
        }
        totaleAnno += importo * mesi;
      }

      // Snapshot
      if (snapshot >= da && snapshot <= a) mensileSnapshot += importo;
    }

    return {
      annoSel,
      snapshot,
      totaleAnno,
      mediaMensile: annoSel != null ? totaleAnno / 12 : 0,
      mensileSnapshot,
    };
  }, [items, filtroAnno]);

  const columns = useMemo<BopExcelColumn<ContrattoInfragruppo>[]>(() => {
    const cols: BopExcelColumn<ContrattoInfragruppo>[] = [];
    if (!isUnica) {
      cols.push({
        key: 'societa',
        header: 'Società',
        accessor: (c) => c.societaNome || String(c.societaId),
        filter: { type: 'text' },
        width: '160px',
        cellClassName: 'bop-text-muted',
        render: (c) => c.societaNome ?? c.societaId,
      });
    }
    cols.push(
      {
        key: 'fornitore',
        header: 'Fornitore',
        accessor: (c) => c.fornitore,
        filter: { type: 'text' },
        render: (c) => <strong>{c.fornitore}</strong>,
      },
      {
        key: 'nomeServizio',
        header: 'Nome servizio',
        accessor: (c) => c.nomeServizio,
        filter: { type: 'text' },
        render: (c) => c.nomeServizio,
      },
      {
        key: 'importoMensile',
        header: 'Importo mensile',
        accessor: (c) => Number(c.importoMensile),
        filter: { type: 'number' },
        width: '140px',
        headerClassName: 'bop-text-right',
        cellClassName: 'bop-text-right',
        render: (c) => formatCurrency(Number(c.importoMensile)),
      },
      {
        key: 'daData',
        header: 'Da data',
        accessor: (c) => c.daData || '',
        filter: { type: 'dateRange' },
        width: '120px',
        render: (c) => fmtData(c.daData),
      },
      {
        key: 'aData',
        header: 'A data',
        accessor: (c) => c.aData || '',
        filter: { type: 'dateRange' },
        width: '120px',
        render: (c) => fmtData(c.aData),
      },
      {
        key: 'attivo',
        header: 'Stato',
        accessor: (c) => c.attivo ? 'ATTIVO' : 'DISATTIVO',
        filter: {
          type: 'enum',
          options: [
            { value: 'ATTIVO',    label: 'Attivo' },
            { value: 'DISATTIVO', label: 'Disattivo' },
          ],
        },
        width: '110px',
        render: (c) => (
          <span className={`bop-badge ${c.attivo ? 'bop-badge--success' : 'bop-badge--neutral'}`}>
            {c.attivo ? 'Attivo' : 'Disattivo'}
          </span>
        ),
      },
    );
    return cols;
  }, [isUnica]);

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h2>Contratti infragruppo</h2>
          <p>Servizi che la società acquista dalle altre aziende del gruppo</p>
        </div>
        {canEdit && (
          <button className="bop-btn bop-btn-primary" onClick={openCreate}>
            + Nuovo contratto
          </button>
        )}
      </div>

      {/* Filtri */}
      <div className="bop-filter-bar">
        {!isUnica && (
          <select
            className="bop-select"
            value={filtroSocietaId}
            onChange={e => setFiltroSocietaId(e.target.value)}
          >
            <option value="">Tutte le società</option>
            {societa.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
          </select>
        )}
        <select className="bop-select" value={filtroAnno} onChange={e => setFiltroAnno(e.target.value)}>
          <option value="">Tutti gli anni</option>
          {anniSelezionabili.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <input
          className="bop-input"
          placeholder="Cerca fornitore…"
          value={filtroFornitore}
          onChange={e => setFiltroFornitore(e.target.value)}
        />
        <label className="bop-flex">
          <input
            type="checkbox"
            checked={mostraDisattivi}
            onChange={e => setMostraDisattivi(e.target.checked)}
          />
          <span>Mostra anche disattivi</span>
        </label>
      </div>

      {error && <div className="bop-inline-error">{error}</div>}

      <BopExcelTable<ContrattoInfragruppo>
        data={items}
        loading={loading}
        rowKey={(c) => c.id}
        onRowClick={canEdit ? (c) => openEdit(c) : undefined}
        initialSort={{ key: 'fornitore', dir: 'asc' }}
        emptyState={<div className="bop-empty-state">Nessun contratto trovato.</div>}
        columns={columns}
        rowActions={(c) => (
          <BopRowActions
            actions={[
              {
                icon: <RowIcon.Trash />,
                label: 'Elimina',
                onClick: () => elimina(c),
                variant: 'danger',
                hidden: !canDelete,
              },
            ]}
          />
        )}
      />

      {items.length > 0 && stats && (
        <div className="contratti-infragruppo-stats">
          {stats.annoSel != null && (
            <>
              <div className="contratti-infragruppo-stat-row">
                <span>Totale anno {stats.annoSel}</span>
                <strong>{formatCurrency(stats.totaleAnno)}</strong>
              </div>
              <div className="contratti-infragruppo-stat-row contratti-infragruppo-stat-row--muted">
                <span>Media mensile (anno / 12)</span>
                <span>{formatCurrency(stats.mediaMensile)}</span>
              </div>
            </>
          )}
          <div className="contratti-infragruppo-stat-row">
            <span>Mensile al {fmtData(stats.snapshot.toISOString())}</span>
            <strong>{formatCurrency(stats.mensileSnapshot)}</strong>
          </div>
        </div>
      )}

      {modalOpen && (
        <Modal
          title={editId ? 'Modifica contratto infragruppo' : 'Nuovo contratto infragruppo'}
          onClose={closeModal}
          footer={
            <>
              <button className="bop-btn bop-btn-secondary" onClick={closeModal}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Salvo…' : 'Salva'}
              </button>
            </>
          }
        >
          {formError && <div className="bop-inline-error">{formError}</div>}

          {!editId && !isUnica && (
            <div className="bop-form-group">
              <label className="bop-label">Società *</label>
              <select
                className="bop-select"
                value={form.societaId}
                onChange={e => setForm(f => ({ ...f, societaId: Number(e.target.value) }))}
              >
                <option value={0}>Seleziona…</option>
                {societa.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
              </select>
            </div>
          )}

          <div className="bop-form-group">
            <label className="bop-label">Fornitore *</label>
            <input
              className="bop-input"
              placeholder="es. Pianinvest Servizi S.r.l."
              value={form.fornitore}
              onChange={e => setForm(f => ({ ...f, fornitore: e.target.value }))}
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Nome servizio *</label>
            <input
              className="bop-input"
              placeholder="es. Servizi IT, Consulenza, Logistica…"
              value={form.nomeServizio}
              onChange={e => setForm(f => ({ ...f, nomeServizio: e.target.value }))}
            />
          </div>

          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Importo mensile (€) *</label>
              <input
                className="bop-input"
                type="number"
                step="0.01"
                min="0"
                placeholder="0,00"
                value={form.importoMensile}
                onChange={e => setForm(f => ({ ...f, importoMensile: e.target.value }))}
              />
            </div>
          </div>

          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Da data *</label>
              <input
                className="bop-input"
                type="date"
                value={form.daData}
                onChange={e => setForm(f => ({ ...f, daData: e.target.value }))}
              />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">A data *</label>
              <input
                className="bop-input"
                type="date"
                value={form.aData}
                onChange={e => setForm(f => ({ ...f, aData: e.target.value }))}
              />
            </div>
          </div>
        </Modal>
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
